import { useState } from 'react';
import { Check, MapPin, Layers, User } from 'lucide-react';
import type {
  CalendarAssignmentRow,
  CalendarTaskRow,
  CalendarWarrantyVisitRow,
} from '@gutterinv/shared';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog.js';
import { cn } from '../lib/utils.js';
import { AssignmentEditModal } from './AssignmentEditModal.js';
import { ScheduledTaskModal } from './ScheduledTaskModal.js';
import { WarrantyVisitModal } from './WarrantyVisitModal.js';
import { taskStyle } from '../lib/task-styles.js';
import { WARRANTY_STYLE } from '../lib/warranty-styles.js';
import { isPostInstallStatus } from '../lib/calendar-dates.js';

export function CalendarDayDetailModal({
  dateISO,
  assignments,
  tasks,
  warranty,
  onClose,
}: {
  dateISO: string;
  assignments: CalendarAssignmentRow[];
  tasks: CalendarTaskRow[];
  warranty: CalendarWarrantyVisitRow[];
  onClose: () => void;
}) {
  // When a job row is clicked, we open the full edit form (crew + start
  // + end + Save / Unassign), layered on top of this modal — same UX as
  // clicking a chip directly on the calendar. AssignmentEditModal
  // handles past/future/installed cases natively (past start dates fail
  // the min=today guard so user must edit forward or unassign; installed
  // jobs render read-only with the locked notice).
  const [editingAssignment, setEditingAssignment] =
    useState<CalendarAssignmentRow | null>(null);
  const [editingTask, setEditingTask] = useState<CalendarTaskRow | null>(null);
  const [editingVisit, setEditingVisit] = useState<CalendarWarrantyVisitRow | null>(null);
  const totalCount = assignments.length + tasks.length + warranty.length;

  const d = new Date(dateISO + 'T00:00:00');
  const dateLabel = d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <>
      <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
        <DialogContent className="w-[560px] max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground">{dateLabel}</DialogTitle>
              <div className="text-sm text-muted-foreground">
                {[
                  assignments.length > 0 && `${assignments.length} job${assignments.length !== 1 ? 's' : ''}`,
                  tasks.length > 0 && `${tasks.length} task${tasks.length !== 1 ? 's' : ''}`,
                  warranty.length > 0 && `${warranty.length} warranty`,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Nothing scheduled'}
              </div>
            </div>
          </div>

          {assignments.length > 0 && (
          <section className="space-y-1.5">
            <h3 className="eyebrow">Jobs</h3>
            {assignments.map((a) => {
                  const peopleLabel =
                    a.people.length > 0
                      ? a.people.map((p) => p.name).join(', ')
                      : a.crew.name;
                  // Match the chip + modal lock semantics. Strikethrough
                  // fires for either case so the office can see at a
                  // glance which rows are "history" before clicking.
                  const isInstalled = isPostInstallStatus(a.job.status);
                  const isCompletedEstimate =
                    a.purpose === 'estimate' && a.completedAt !== null;
                  const isDone = isInstalled || isCompletedEstimate;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setEditingAssignment(a)}
                      className={cn(
                        'w-full rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-primary/[0.06] hover:border-primary/30',
                        a.purpose === 'estimate' && 'border-dashed',
                        isDone && 'opacity-60',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {isDone && (
                          <Check
                            className="h-3.5 w-3.5 shrink-0 text-foreground/70"
                            aria-label={isInstalled ? 'installed' : 'estimate submitted'}
                          />
                        )}
                        <div
                          className={cn(
                            'text-sm font-semibold text-foreground',
                            isDone && 'line-through',
                          )}
                        >
                          {a.job.displayName}
                        </div>
                        {a.purpose === 'estimate' && (
                          <span className="text-[0.625rem] uppercase tracking-wider text-muted-foreground">
                            Est
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground/80">
                        <User className="h-3 w-3 shrink-0 text-muted-foreground" />
                        {peopleLabel}
                      </div>
                      <div
                        className={cn(
                          'text-xs text-muted-foreground',
                          isDone && 'line-through',
                        )}
                      >
                        {a.job.customerName}
                      </div>
                      {a.job.address && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 shrink-0" />
                          {a.job.address}
                        </div>
                      )}
                      {a.phase && (
                        <>
                          <div
                            className="mt-1 inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/[0.06] px-1.5 py-0.5 text-[0.6875rem] font-medium text-primary"
                            title={a.phase.itemsSummary || undefined}
                          >
                            <Layers className="h-3 w-3" />
                            Phase {a.phase.phaseNumber} · {a.phase.itemCount} item
                            {a.phase.itemCount === 1 ? '' : 's'}
                          </div>
                          {/* Phase 1.2 Item 8: render the structured items
                              list when present. Each row truncates to
                              defend against long freehand_text. Falls
                              back to the comma-joined summary if items
                              is absent (legacy / defensive). String()
                              renders whole numbers as "12" not "12.00"
                              — see plan for the float8 cast rationale. */}
                          {a.phase.items && a.phase.items.length > 0 ? (
                            <ul className="mt-1 space-y-0.5">
                              {a.phase.items.map((it, idx) => (
                                <li
                                  key={`${idx}-${it.name}`}
                                  className="truncate text-[0.6875rem] text-muted-foreground"
                                  title={`${it.name} × ${String(it.quantity)} ${it.unit}`}
                                >
                                  {it.name} × {String(it.quantity)} {it.unit}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            a.phase.itemsSummary && (
                              <div
                                className="mt-0.5 truncate text-[0.6875rem] text-muted-foreground"
                                title={a.phase.itemsSummary}
                              >
                                {a.phase.itemsSummary}
                              </div>
                            )
                          )}
                        </>
                      )}
                      {a.assignedDateStart !== a.assignedDateEnd && (
                        <div className="mt-0.5 text-[0.6875rem] uppercase tracking-wider text-taupe">
                          {a.assignedDateStart} – {a.assignedDateEnd}
                        </div>
                      )}
                    </button>
                  );
            })}
          </section>
          )}

          {tasks.length > 0 && (
            <section className="space-y-1.5">
              <h3 className="eyebrow">Tasks</h3>
              {tasks.map((t) => {
                const st = taskStyle(t.taskType);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setEditingTask(t)}
                    className={cn(
                      'w-full rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-primary/[0.06] hover:border-primary/30',
                      t.completedAt && 'opacity-60',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <st.Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <div className={cn('text-sm font-semibold text-foreground', t.completedAt && 'line-through')}>
                        {st.label}
                      </div>
                      {t.completedAt && (
                        <Check className="h-3.5 w-3.5 shrink-0 text-foreground/70" aria-label="done" />
                      )}
                    </div>
                    {t.people.length > 0 && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground/80">
                        <User className="h-3 w-3 shrink-0 text-muted-foreground" />
                        {t.people.map((p) => p.name).join(', ')}
                      </div>
                    )}
                    {t.notes && (
                      <div className="mt-0.5 truncate text-xs text-muted-foreground" title={t.notes}>
                        {t.notes}
                      </div>
                    )}
                  </button>
                );
              })}
            </section>
          )}

          {warranty.length > 0 && (
            <section className="space-y-1.5">
              <h3 className="eyebrow">Warranty</h3>
              {warranty.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setEditingVisit(v)}
                  className={cn(
                    'w-full rounded-md border border-border px-3 py-2 text-left transition-colors hover:bg-primary/[0.06] hover:border-primary/30',
                    v.completedAt && 'opacity-60',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <WARRANTY_STYLE.Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className={cn('text-sm font-semibold text-foreground', v.completedAt && 'line-through')}>
                      {v.customerName}
                    </div>
                    {v.completedAt && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-foreground/70" aria-label="done" />
                    )}
                  </div>
                  {v.people.length > 0 && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground/80">
                      <User className="h-3 w-3 shrink-0 text-muted-foreground" />
                      {v.people.map((p) => p.name).join(', ')}
                    </div>
                  )}
                  {v.address && (
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {v.address}
                    </div>
                  )}
                  {v.visitDateStart !== v.visitDateEnd && (
                    <div className="mt-0.5 text-[0.6875rem] uppercase tracking-wider text-taupe">
                      {v.visitDateStart} – {v.visitDateEnd}
                    </div>
                  )}
                </button>
              ))}
            </section>
          )}

          {totalCount === 0 && (
            <div className="text-center text-sm text-muted-foreground py-4">
              Nothing scheduled for this day.
            </div>
          )}

          <p className="text-center text-[0.6875rem] text-muted-foreground">
            Click any item to edit it.
          </p>
        </DialogContent>
      </Dialog>

      {editingAssignment && (
        <AssignmentEditModal
          assignment={editingAssignment}
          onClose={() => setEditingAssignment(null)}
        />
      )}
      {editingTask && (
        <ScheduledTaskModal task={editingTask} onClose={() => setEditingTask(null)} />
      )}
      {editingVisit && (
        <WarrantyVisitModal
          visit={editingVisit}
          jobId={editingVisit.jobId}
          onClose={() => setEditingVisit(null)}
        />
      )}
    </>
  );
}
